import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FiHeadphones, FiFileText, FiDollarSign, FiArrowRight, FiMessageSquare } from 'react-icons/fi';
import Homemenu from '../../../helpers/home.js';
import { getUserSubs, getUser, setRecentCheckout } from '../../../store/user.js';
import { getPrices } from '../../../store/price.js';
import { useFormPackage } from '../../../hooks/useFormPackage.js';
import { useTypeformResponses } from '../../../hooks/useTypeformResponses.js';
import Forms from '../../../helpers/forms.js';
import SublyApi from '../../../helpers/Api.js';
import { formatCurrency } from '../../spending-tracker/shared/constants.js';

import VerifyModal from '../v1/VerifyModal';

// Dashboard card wrapper with accent bar, icon, and ARIA support
const DashboardCard = ({ icon: Icon, title, subtitle, children, onClick, ariaLabel }) => (
	<div
		className="bg-white rounded-2xl shadow-md border border-gray-100 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-500 cursor-pointer group"
		onClick={onClick}
		role="button"
		tabIndex={0}
		onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
		aria-label={ariaLabel || title}
	>
		{/* Top accent bar */}
		<div className="h-1 w-full bg-gray-300 rounded-t-2xl group-hover:bg-[#CC1DB9] transition-colors duration-500" />
		<div className="p-6 md:p-8 h-full flex flex-col">
			<div className="flex items-start gap-4 mb-6">
				<div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#CC1DB9] transition-colors duration-500">
					<Icon className="w-5 h-5 text-white" />
				</div>
				<div>
					<h4 className="text-lg font-glacial text-gray-900">{title}</h4>
					{subtitle && (
						<p className="text-sm font-montserrat-regular text-gray-600 mt-0.5">{subtitle}</p>
					)}
				</div>
			</div>
			<div className="flex-1 flex flex-col">
				{children}
			</div>
		</div>
	</div>
);

// Service/category row with status badge
const ServiceRow = ({ service, status, statusColor, price }) => (
	<div className="flex items-center justify-between text-sm py-2.5 border-b border-gray-50 last:border-b-0">
		<span className="font-montserrat-regular text-gray-600">{service}</span>
		<div className="flex items-center gap-3">
			{price && (
				<span className="font-montserrat-regular text-gray-500">${price}/mo</span>
			)}
			<span className={`font-montserrat-medium ${statusColor}`}>{status}</span>
		</div>
	</div>
);

// Card CTA footer with arrow icon
const CardFooter = ({ label, onClick, disabled }) => (
	<div className="border-t border-gray-100 pt-4 mt-auto">
		<button
			className={`text-sm font-montserrat-medium hover:opacity-80 transition-all inline-flex items-center gap-1 ${
				disabled ? 'text-gray-400' : 'text-[#CC1DB9]'
			}`}
			onClick={(e) => {
				e.stopPropagation();
				if (!disabled && onClick) onClick();
			}}
		>
			{label}
			<FiArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
		</button>
	</div>
);

const Home = () => {
	const dispatch = useDispatch();
	const token = useSelector((st) => st.user.token);
	const currentUser = useSelector((st) => st.user.currentUser);
	const stripeSubs = useSelector((st) => st.user.stripeSubs);
	const recentCheckout = useSelector((st) => st.user.recentCheckout);
	const history = useHistory();

	const formPackage = useFormPackage(stripeSubs, Forms);
	useTypeformResponses(formPackage, currentUser, token, dispatch);

	// Initialize access state for each menu item
	const INITIAL_ACCESS_STATE = Homemenu.menus.reduce((acc, menu) => {
		acc[menu.accessKey] = menu.initialAccess;
		return acc;
	}, {});

	const [access, setAccess] = useState(INITIAL_ACCESS_STATE);
	const [formSubmissions, setFormSubmissions] = useState([]);
	const [formSubmissionsLoading, setFormSubmissionsLoading] = useState(true);
	const [expenseSummary, setExpenseSummary] = useState(null);
	const [expensesLoading, setExpensesLoading] = useState(true);
	const justClearedCheckout = useRef(false);

	const handleChatClick = () => {
		const slackWeb = 'https://janerothe.slack.com/';
		const slackApp = 'slack://open?team=janerothe';

		// Open web URL immediately (user gesture context — won't be popup-blocked)
		const webTab = window.open(slackWeb, '_blank', 'noreferrer');

		// Simultaneously try the desktop app via hidden iframe
		const iframe = document.createElement('iframe');
		iframe.style.display = 'none';
		iframe.src = slackApp;
		document.body.appendChild(iframe);

		// If the desktop app opens, close the extra web tab
		const onBlur = () => {
			try { if (webTab) webTab.close(); } catch (ignored) {}
			window.removeEventListener('blur', onBlur);
		};
		window.addEventListener('blur', onBlur);

		// Cleanup after 2 seconds
		setTimeout(() => {
			try { document.body.removeChild(iframe); } catch (ignored) {}
			window.removeEventListener('blur', onBlur);
		}, 2000);
	};

	// Get user subscriptions from Stripe
	useEffect(() => {
		if (currentUser?.customerId && token) {
			dispatch(getUserSubs({ customerId: currentUser.customerId, token }));
		}
	}, [currentUser?.customerId, token, dispatch]);

	// Retrieve all products from Stripe
	useEffect(() => {
		if (token) {
			dispatch(getPrices({ token }));
		}
	}, [token, dispatch]);

	// Update user credentials when token changes
	useEffect(() => {
		if (currentUser?.username && token) {
			dispatch(getUser({ username: currentUser.username, token }));
		}
	}, [currentUser?.username, token, dispatch]);

	// Fetch available forms (using access control)
	useEffect(() => {
		// Skip the re-fetch caused by us clearing recentCheckout
		if (justClearedCheckout.current) {
			justClearedCheckout.current = false;
			return;
		}

		const fetchAvailableForms = async () => {
			if (!currentUser?.username || !token) {
				return;
			}

			try {
				const params = recentCheckout ? { bypass_cache: true } : {};
				const response = await SublyApi.request('api/forms', params, 'get');

				if (response && response.forms) {
					// Update myForms access based on whether user has any accessible forms
					setAccess((prevAccess) => ({
						...prevAccess,
						myForms: response.forms.length > 0
					}));
				}
				if (recentCheckout) {
					justClearedCheckout.current = true;
					dispatch(setRecentCheckout(false));
				}
			} catch (err) {
				console.error('Error fetching available forms:', err);
				setAccess((prevAccess) => ({
					...prevAccess,
					myForms: false
				}));
			}
		};

		fetchAvailableForms();
	}, [currentUser?.username, token, recentCheckout, dispatch]);

	// Fetch form submissions
	useEffect(() => {
		const fetchFormSubmissions = async () => {
			if (!currentUser?.username || !token) {
				setFormSubmissionsLoading(false);
				return;
			}

			try {
				setFormSubmissionsLoading(true);
				const response = await SublyApi.getFormSubmissions(
					currentUser.username,
					token
				);

				if (response && response.submissions) {
					setFormSubmissions(response.submissions);
				}
			} catch (err) {
				console.error('Error fetching form submissions:', err);
				setFormSubmissions([]);
			} finally {
				setFormSubmissionsLoading(false);
			}
		};

		fetchFormSubmissions();
	}, [currentUser?.username, token]);

	// Fetch expense summary for current month
	useEffect(() => {
		const fetchExpenseSummary = async () => {
			if (!currentUser?.username || !token) {
				setExpensesLoading(false);
				return;
			}

			try {
				setExpensesLoading(true);
				// Get current month date range
				const now = new Date();
				const year = now.getFullYear();
				const month = String(now.getMonth() + 1).padStart(2, '0');
				const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();

				const filters = {
					start_date: `${year}-${month}-01`,
					end_date: `${year}-${month}-${String(lastDay).padStart(2, '0')}`
				};

				const summary = await SublyApi.getExpenseSummary(filters);
				setExpenseSummary(summary);
			} catch (err) {
				console.error('Error fetching expense summary:', err);
				setExpenseSummary(null);
			} finally {
				setExpensesLoading(false);
			}
		};

		fetchExpenseSummary();
	}, [currentUser?.username, token]);

	const handleModuleClick = (accessKey, url) => {
		if (!access[accessKey]) {
			history.push('/my-services');
			return;
		}
		history.push(url);
	};

	// Get subscription status and color
	const getSubscriptionStatus = (subscription) => {
		if (subscription.cancel_at_period_end) {
			return { status: 'Canceling', color: 'text-gray-500' };
		}

		switch (subscription.status) {
			case 'active':
				return { status: 'Active', color: 'text-gray-900' };
			case 'canceled':
				return { status: 'Canceled', color: 'text-red-600' };
			case 'trialing':
				return { status: 'Trial', color: 'text-[#CC1DB9]' };
			case 'past_due':
				return { status: 'Past Due', color: 'text-red-500' };
			default:
				return { status: subscription.status || 'Unknown', color: 'text-gray-500' };
		}
	};

	// Get top 3 expense categories sorted by amount
	const getTopExpenseCategories = () => {
		if (!expenseSummary?.byCategory || expenseSummary.byCategory.length === 0) {
			return [];
		}

		// Sort categories by amount descending and take top 3
		return [...expenseSummary.byCategory]
			.sort((a, b) => b.total - a.total)
			.slice(0, 3);
	};

	// Check if there are any form submissions in the last 30 days
	const hasRecentSubmissions = () => {
		if (!formSubmissions?.length) return false;

		const thirtyDaysAgo = new Date();
		thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

		return formSubmissions.some(submission => {
			const submissionDate = new Date(submission.submitted_at || submission.created_at);
			return submissionDate >= thirtyDaysAgo;
		});
	};

	// Get form submission stats
	const getFormSubmissionStats = () => {
		if (!formSubmissions || formSubmissions.length === 0) {
			return { thisMonth: 0, thisYear: 0, lastSubmission: null };
		}

		// Calculate date 30 days ago
		const thirtyDaysAgo = new Date();
		thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

		// Calculate start of current year
		const startOfYear = new Date(new Date().getFullYear(), 0, 1);

		// Count this month's submissions (last 30 days)
		const thisMonthCount = formSubmissions.filter(submission => {
			const submissionDate = new Date(submission.submitted_at || submission.created_at);
			return submissionDate >= thirtyDaysAgo;
		}).length;

		// Count this year's submissions
		const thisYearCount = formSubmissions.filter(submission => {
			const submissionDate = new Date(submission.submitted_at || submission.created_at);
			return submissionDate >= startOfYear;
		}).length;

		// Find most recent submission
		const sortedSubmissions = [...formSubmissions].sort((a, b) => {
			const dateA = new Date(a.submitted_at || a.created_at);
			const dateB = new Date(b.submitted_at || b.created_at);
			return dateB - dateA;
		});

		const lastSubmission = sortedSubmissions.length > 0 ? sortedSubmissions[0] : null;

		return { thisMonth: thisMonthCount, thisYear: thisYearCount, lastSubmission };
	};

	// Format relative date (e.g., "2 days ago")
	const formatRelativeDate = (date) => {
		if (!date) return null;

		const submissionDate = new Date(date);
		const now = new Date();
		const diffTime = Math.abs(now - submissionDate);
		const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

		if (diffDays === 0) return 'Today';
		if (diffDays === 1) return 'Yesterday';
		if (diffDays < 7) return `${diffDays} days ago`;
		if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
		return `${Math.floor(diffDays / 30)} months ago`;
	};

	const formStats = getFormSubmissionStats();

	return (
		<div data-testid="home" className="min-h-screen bg-stone-50">
			<VerifyModal />

			{/* Main Content Container */}
			<div className="w-full">
				<main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

					{/* Welcome Section */}
					<section className="mb-12 pt-4 pb-2" aria-labelledby="welcome-heading">
						<div>
							<h1 id="welcome-heading" className="text-4xl font-glacial tracking-tight text-black mb-4">
								Welcome back{currentUser?.firstName ? `, ${currentUser.firstName}` : ''}
							</h1>
							<p className="text-base font-montserrat-regular text-gray-500 max-w-2xl leading-relaxed">
								Your marketing dashboard &mdash; services, forms, and spend at a glance.
							</p>
							<button
								type="button"
								onClick={handleChatClick}
								className="text-[#CC1DB9] font-montserrat-medium hover:opacity-80 inline-flex items-center gap-1.5 mt-3 text-sm cursor-pointer bg-transparent border-none p-0"
							>
								<FiMessageSquare className="w-3.5 h-3.5" />
								Chat with us
							</button>
						</div>
					</section>

					{/* Dashboard Overview */}
					<section className="mb-12" aria-labelledby="overview-heading">
						<h3 id="overview-heading" className="text-xs font-montserrat-medium tracking-[0.2em] text-gray-400 uppercase mb-8">Dashboard Overview</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">

							{/* My Services Card */}
							<DashboardCard
								icon={FiHeadphones}
								title="My Services"
								subtitle="Active subscriptions"
								onClick={() => history.push('/my-services')}
								ariaLabel="View my services and subscriptions"
							>
								{stripeSubs && stripeSubs.length > 0 ? (
									<>
										<div className="space-y-0 mb-6">
											{stripeSubs.slice(0, 3).map((sub) => {
												const { status, color } = getSubscriptionStatus(sub);
												const price = sub.items?.data?.[0]?.price?.unit_amount
													? (sub.items.data[0].price.unit_amount / 100).toFixed(2)
													: null;
												return (
													<ServiceRow
														key={sub.id || sub.productName}
														service={sub.productName || 'Subscription'}
														status={status}
														statusColor={color}
														price={price}
													/>
												);
											})}
										</div>
										<CardFooter
											label={stripeSubs.length > 3 ? 'View All Services' : '+ Add Services'}
											onClick={() => history.push('/my-services')}
										/>
									</>
								) : (
									<>
										<div className="flex-1 flex items-center justify-center mb-6">
											<div className="text-center">
												<p className="font-montserrat-regular text-gray-500 text-sm mb-2">No active subscriptions</p>
												<p className="font-montserrat-regular text-gray-400 text-xs">Get started by adding a service</p>
											</div>
										</div>
										<CardFooter
											label="+ Add Services"
											onClick={() => history.push('/my-services')}
										/>
									</>
								)}
							</DashboardCard>

							{/* My Forms Card */}
							<DashboardCard
								icon={FiFileText}
								title="My Forms"
								subtitle="Submissions & activity"
								onClick={() => handleModuleClick('myForms', '/forms')}
								ariaLabel="View my forms and submissions"
							>
								{formSubmissionsLoading ? (
									<div className="flex-1 flex items-center justify-center mb-6">
										<div className="text-center">
											<div className="w-8 h-8 rounded-full animate-spin border-t-2 border-gray-300 mx-auto mb-2"></div>
											<p className="font-montserrat-regular text-gray-400 text-xs">Loading submissions...</p>
										</div>
									</div>
								) : hasRecentSubmissions() ? (
									<>
										{/* Stats Section */}
										<div className="space-y-3 mb-6">
											<div className="flex items-center justify-between">
												<span className="text-sm font-montserrat-regular text-gray-600">This Month</span>
												<span className="text-lg font-glacial text-gray-900">
													{formStats.thisMonth}
													<span className="sr-only"> submissions this month</span>
												</span>
											</div>
											<div className="flex items-center justify-between">
												<span className="text-sm font-montserrat-regular text-gray-600">This Year</span>
												<span className="text-lg font-glacial text-gray-900">
													{formStats.thisYear}
													<span className="sr-only"> submissions this year</span>
												</span>
											</div>
											{formStats.lastSubmission && (
												<div className="flex items-center justify-between">
													<span className="text-sm font-montserrat-regular text-gray-600">Last Submission</span>
													<span className="text-sm font-montserrat-regular text-gray-700">
														{formatRelativeDate(formStats.lastSubmission.submitted_at || formStats.lastSubmission.created_at)}
													</span>
												</div>
											)}
										</div>

										<CardFooter
											label={access.myForms ? 'See Details' : 'Upgrade Required'}
											onClick={() => history.push('/forms')}
											disabled={!access.myForms}
										/>
									</>
								) : (
									<>
										<div className="flex-1 flex items-center justify-center mb-6">
											<div className="text-center">
												<p className="font-montserrat-regular text-gray-500 text-sm mb-2">No forms submitted this month</p>
												<p className="font-montserrat-regular text-gray-400 text-xs">Start by submitting a form</p>
											</div>
										</div>
										<CardFooter
											label={access.myForms ? 'Submit Form' : 'Upgrade Required'}
											onClick={() => history.push('/forms')}
											disabled={!access.myForms}
										/>
									</>
								)}
							</DashboardCard>

							{/* Spending Tracker Card */}
							<DashboardCard
								icon={FiDollarSign}
								title="Spending Tracker"
								subtitle="Monthly investment"
								onClick={() => handleModuleClick('spendingTracker', '/spending-tracker')}
								ariaLabel="View spending tracker and expenses"
							>
								{expensesLoading ? (
									<div className="flex-1 flex items-center justify-center mb-6">
										<div className="text-center">
											<div className="w-8 h-8 rounded-full animate-spin border-t-2 border-gray-300 mx-auto mb-2"></div>
											<p className="font-montserrat-regular text-gray-400 text-xs">Loading expenses...</p>
										</div>
									</div>
								) : expenseSummary && expenseSummary.totalAmount > 0 ? (
									<>
										<div className="mb-4">
											<p className="text-4xl font-glacial tracking-tight text-gray-900 mb-2">
												{formatCurrency(expenseSummary.totalAmount)}
											</p>
											<p className="text-xs font-montserrat-medium tracking-[0.15em] uppercase text-gray-500">Total This Month</p>
										</div>
										<div className="space-y-0 mb-6">
											{getTopExpenseCategories().map((category) => (
												<ServiceRow
													key={category.category}
													service={category.category}
													status={formatCurrency(category.total)}
													statusColor="text-gray-900"
												/>
											))}
										</div>
										<CardFooter label="See Details" />
									</>
								) : (
									<>
										<div className="flex-1 flex items-center justify-center mb-6">
											<div className="text-center">
												<p className="font-montserrat-regular text-gray-500 text-sm mb-2">No expenses for this month</p>
												<p className="font-montserrat-regular text-gray-400 text-xs">Track your marketing investments</p>
											</div>
										</div>
										<CardFooter label="See Details" />
									</>
								)}
							</DashboardCard>

						</div>
					</section>

				</main>
			</div>
		</div>
	);
};

export default Home;
